/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Container,
  Grid,
  Box,
  makeStyles
} from '@material-ui/core';
import toast from 'react-hot-toast';
import Page from 'src/components/Page';
import NotFoundCard from 'src/components/NotFoundCard';
import { useLocation, useNavigate, useParams } from 'react-router';
import Pages from 'src/api/pages';
import User from 'src/api/user';
import Profile from './Profile';
import ProfileDetails from './ProfileDetails';
import BankDetails from './BankDetails';
import DocumentDetails from './DocumentDetails';
import VehicleDetails from './VehicleDetails';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const CustomerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const [notFound, setNotFound] = useState(false);
  const [pageName, setPageName] = useState('Customer');
  const [customer, setCustomer] = useState({});
  const [summary, setCustomerSummary] = useState({});
  const [vehicle, setVehicle] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [profileFormdata, setProfileFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const loadUser = async () => {
    try {
      setIsLoading(true);
      setNotFound(false);
      const data = await Pages.getCustomerPage(id);
      setPageName(`${data.user.firstName} profile details`);
      setCustomer(data.user);
      // TODO: abstract feature in a hook
      setProfileFormData(data.user);
      setCustomerSummary(data.summary);
      setVehicle(data.vehicle[0] || {});
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error.response?.status === 404) {
        setNotFound(true);
        return;
      }
      // Interceptor already toasted network errors (ISSUE-0201).
      if (error.response) {
        const { data: errData } = error.response;
        toast.error(
          errData?.errors?.[0] || errData?.message || 'Something went wrong'
        );
      }
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleVerification = async data => {
    try {
      const res = await User.verifyUser(id, data);
      setCustomer(prev => ({ ...prev, ...res.data }));
      toast.success(`Updated successfully!`);
    } catch (err) {
      // Interceptor already toasted network errors (ISSUE-0201).
      if (err.response) {
        const { data: errData } = err.response;
        toast.error(
          errData?.errors?.[0] || errData?.message || 'Something went wrong'
        );
      }
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateProfileDetails = async () => {
    const changedFields = Object.entries(profileFormdata).reduce(
      (fields, curKeyValuePair) => {
        const [key, value] = curKeyValuePair;
        if (value !== customer[key]) fields[key] = value;
        return fields;
      },
      {}
    );

    // there are no changed fields, no point sending request
    if (!Object.keys(changedFields).length) return;

    try {
      setIsUpdating(true);
      const { data } = await User.updateUser(id, changedFields);
      setCustomer(data);
      setIsUpdating(false);
    } catch (error) {
      setIsUpdating(false);
      // Let ProfileDetails show the error and stay in edit mode (ISSUE-0145).
      throw error;
    }
  };

  if (isLoading) {
    return (
      <Page className={classes.root} title={pageName}>
        <Box textAlign="center" py={10}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (notFound) {
    return (
      <NotFoundCard
        title="Customer"
        message="Customer not found."
        backLabel="Go back"
        // Drivers share this page, so go back to wherever the admin came
        // from; a directly opened URL has no history to go back to.
        onBack={() =>
          location.key === 'default'
            ? navigate('/app/customers')
            : navigate(-1)
        }
      />
    );
  }

  return (
    <Page className={classes.root} title={pageName}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item lg={4} md={6} xs={12}>
            <Profile
              customer={customer}
              formData={profileFormdata}
              summary={summary}
              handleVerification={handleVerification}
            />
          </Grid>
          <Grid item lg={8} md={6} xs={12}>
            <ProfileDetails
              formData={profileFormdata}
              customer={customer}
              onChange={handleChange}
              onSave={updateProfileDetails}
              isUpdating={isUpdating}
              handleVerification={handleVerification}
            />
            <BankDetails customer={customer} />
            <DocumentDetails
              handleVerification={handleVerification}
              customer={customer}
            />
            <VehicleDetails vehicle={vehicle} />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default CustomerView;
